import React, { useState } from 'react';
import { H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Row, FormGroup, Label, Input, Collapse, Button, Badge, Alert } from 'reactstrap';
import { ChevronDown, ChevronUp, User, Truck, FileText, MapPin, Phone, Calendar, Shield } from 'react-feather';

const AccidentFileInformations = ({ arbitrationId, arbitrationData }) => {
    const [openSections, setOpenSections] = useState({
        customer: true,
        vehicle: true,
        accident: true,
        insurance: true,
        file: true
    });

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Belirtilmemiş';
        return new Date(dateString).toLocaleString('tr-TR');
    };

    // Helper function to format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return 'Belirtilmemiş';
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    // Extract data from arbitration response
    const accidentFile = arbitrationData?.accidentFile;
    const customer = accidentFile?.customer;
    const accident = accidentFile?.accident;
    const licensePlate = accident?.licensePlate;

    if (!accidentFile) {
        return (
            <Row className="mt-4">
                <Col sm='12'>
                    <Alert color="warning">
                        <i className="fa fa-exclamation-triangle me-2"></i>
                        Kaza dosyası bilgileri bulunamadı.
                    </Alert>
                </Col>
            </Row>
        );
    }

    const CollapsibleSection = ({ title, isOpen, onToggle, icon, children, badgeText }) => (
        <Card className="mb-3">
            <CardHeader 
                className="cursor-pointer d-flex justify-content-between align-items-center p-3"
                onClick={onToggle}
                style={{ backgroundColor: isOpen ? '#f8f9fa' : '#ffffff' }}
            >
                <div className="d-flex align-items-center">
                    {icon}
                    <H5 className="mb-0 ms-2">{title}</H5>
                    {badgeText && <Badge color="info" className="ms-2">{badgeText}</Badge>}
                </div>
                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>
                    {children}
                </CardBody>
            </Collapse>
        </Card>
    );

    return (
        <Row className="mt-4">
            <Col sm='12'>
                <Card>
                    <CardHeader>
                        <H5>
                            <i className="fa fa-folder-open me-2"></i>
                            Kaza Dosyası Bilgileri
                        </H5>
                        <span>Dosya No: <strong>{accidentFile.fileNumber}</strong> | Durum: <Badge color="success">{accidentFile.fileStatus}</Badge></span>
                    </CardHeader>
                    <CardBody>
                        {/* Customer Information Section */}
                        <CollapsibleSection
                            title="Müvekkil Bilgileri"
                            isOpen={openSections.customer}
                            onToggle={() => toggleSection('customer')}
                            icon={<User size={20} />}
                            badgeText={customer?.customerType}
                        >
                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-user me-1"></i>Müvekkil Türü</Label>
                                        <Input type="text" value={customer?.customerType || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-id-card me-1"></i>Kimin Referansı?</Label>
                                        <Input type="text" value={customer?.reference || ''} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {customer?.customerType === 'Bireysel' && (
                                <Row>
                                    <Col md="6">
                                        <FormGroup className="mb-3">
                                            <Label><i className="fa fa-user me-1"></i>Müvekkil Adı</Label>
                                            <Input type="text" value={customer?.clientName || ''} disabled />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup className="mb-3">
                                            <Label><i className="fa fa-id-card me-1"></i>TC Kimlik Numarası</Label>
                                            <Input type="text" value={customer?.identityNumber || ''} disabled />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}

                            {customer?.customerType === 'Kurumsal' && (
                                <>
                                    <Row>
                                        <Col md="4">
                                            <FormGroup className="mb-3">
                                                <Label><i className="fa fa-building me-1"></i>Şirket Adı</Label>
                                                <Input type="text" value={customer?.companyName || ''} disabled />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup className="mb-3">
                                                <Label><i className="fa fa-hashtag me-1"></i>MERSIS Numarası</Label>
                                                <Input type="text" value={customer?.crsNumber || ''} disabled />
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup className="mb-3">
                                                <Label><i className="fa fa-hashtag me-1"></i>Vergi Numarası</Label>
                                                <Input type="text" value={customer?.taxNumber || ''} disabled />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="12">
                                            <FormGroup className="mb-3">
                                                <Label><i className="fa fa-user-tie me-1"></i>Firma Yetkilisi</Label>
                                                <Input type="text" value={customer?.companyRepresentative || 'Belirtilmemiş'} disabled />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-percent me-1"></i>Sözleşme Oranı (%)</Label>
                                        <Input type="text" value={customer?.contractRate || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-calendar me-1"></i>Kayıt Tarihi</Label>
                                        <Input type="text" value={formatDate(customer?.created)} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Email Information */}
                            {customer?.emails && customer.emails.length > 0 && (
                                <Row>
                                    <Col md="12">
                                        <Label><i className="fa fa-envelope me-1"></i>E-Mail Bilgileri</Label>
                                        {customer.emails.map((email, index) => (
                                            <div key={index} className="mb-2">
                                                <Input 
                                                    type="text" 
                                                    value={`${email.emailDescription}: ${email.email}`} 
                                                    disabled 
                                                />
                                            </div>
                                        ))}
                                    </Col>
                                </Row>
                            )}

                            {/* Phone Information */}
                            {/* Phone Information */}
                            {customer?.phones && customer.phones.length > 0 && (
                                <Row>
                                    <Col md="12">
                                        <Label><Phone size={16} className="me-1" />Telefon Bilgileri</Label>
                                        {customer.phones.map((phone, index) => (
                                            <div key={index} className="mb-2">
                                                <Input 
                                                    type="text" 
                                                    value={`${phone.phoneDescription}: ${phone.phone}`} 
                                                    disabled 
                                                />
                                            </div>
                                        ))}
                                    </Col>
                                </Row>
                            )}

                            {/* Address Information */}
                            {customer?.address && (
                                <Row>
                                    <Col md="12">
                                        <FormGroup className="mb-3">
                                            <Label><MapPin size={16} className="me-1" />Adres</Label>
                                            <Input type="textarea" value={customer.address} disabled rows="2" />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}

                            {/* IBAN Information */}
                            {customer?.ibans && customer.ibans.length > 0 && (
                                <Row>
                                    <Col md="12">
                                        <Label><i className="fa fa-university me-1"></i>IBAN Bilgileri</Label>
                                        {customer.ibans.map((iban, index) => (
                                            <div key={index} className="mb-2">
                                                <Input 
                                                    type="text" 
                                                    value={`${iban.ibanDescription}: ${iban.iban}`} 
                                                    disabled 
                                                />
                                            </div>
                                        ))}
                                    </Col>
                                </Row>
                            )}
                        </CollapsibleSection>

                        {/* Vehicle Information Section */}
                        <CollapsibleSection
                            title="Araç Bilgileri"
                            isOpen={openSections.vehicle}
                            onToggle={() => toggleSection('vehicle')}
                            icon={<Truck size={20} />}
                            badgeText={licensePlate?.licensePlateNumber}
                        >
                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-truck me-1"></i>Plaka</Label>
                                        <Input type="text" value={licensePlate?.licensePlateNumber || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-info-circle me-1"></i>Plaka Durumu</Label>
                                        <Input type="text" value={licensePlate?.licensePlateStatus || ''} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-hashtag me-1"></i>Şasi Numarası</Label>
                                        <Input type="text" value={licensePlate?.carchassisNumber || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-user me-1"></i>Araç Sahibi</Label>
                                        <Input type="text" value={licensePlate?.carOwner || ''} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-truck me-1"></i>Marka</Label>
                                        <Input type="text" value={licensePlate?.carBrand || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-truck me-1"></i>Model</Label>
                                        <Input type="text" value={licensePlate?.carModel || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-calendar me-1"></i>Model Yılı</Label>
                                        <Input type="text" value={licensePlate?.carYear || ''} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Vehicle Status Checks */}
                            <Row>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Değer Kaybı Talebi</Label>
                                        <Input 
                                            type="text" 
                                            value={licensePlate?.isThereLovClaim ? 'Var' : 'Yok'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Haciz Durumu</Label>
                                        <Input 
                                            type="text" 
                                            value={licensePlate?.lpImpeachmentStatus ? 'Var' : 'Yok'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Araç Satılmış mı?</Label>
                                        <Input 
                                            type="text" 
                                            value={licensePlate?.hasCarSoldBefore ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CollapsibleSection>

                        {/* Accident Information Section */}
                        <CollapsibleSection
                            title="Kaza Bilgileri"
                            isOpen={openSections.accident}
                            onToggle={() => toggleSection('accident')}
                            icon={<FileText size={20} />}
                            badgeText={accident?.accidentNumber}
                        >
                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><i className="fa fa-hashtag me-1"></i>Kaza No</Label>
                                        <Input type="text" value={accident?.accidentNumber || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label><Calendar size={16} className="me-1" />Kaza Tarihi</Label>
                                        <Input type="text" value={formatDate(accident?.accidentDate)} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Counterparty Information */}
                            <Row>
                                <Col md="12">
                                    <h6 className="mb-3"><i className="fa fa-users me-2"></i>Karşı Taraf Bilgileri</h6>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Türü</Label>
                                        <Input type="text" value={accident?.counterpartyType || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Kimlik/Vergi No</Label>
                                        <Input type="text" value={accident?.counterpartyIdentificationNumber || accident?.counterpartyTaxNumber || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Araç Sahibi</Label>
                                        <Input type="text" value={accident?.counterpartyCarOwner || ''} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Plaka</Label>
                                        <Input type="text" value={accident?.counterpartyLicensePlateNumber || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Adres</Label>
                                        <Input type="text" value={accident?.counterpartyAddress || 'Belirtilmemiş'} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Fault Rates */}
                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Bizim Kusur Oranı (%)</Label>
                                        <Input type="text" value={accident?.ourFaultRate || '0'} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Kusur Oranı (%)</Label>
                                        <Input type="text" value={accident?.counterPartyFaultRate || '0'} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Document Status */}
                            <Row>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Dava Evrakı</Label>
                                        <Input 
                                            type="text" 
                                            value={accident?.isTrialDocument ? 'Var' : 'Yok'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>İş Evrakı</Label>
                                        <Input 
                                            type="text" 
                                            value={accident?.isWorkDocument ? 'Var' : 'Yok'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>LOR Çalışılacak mı?</Label>
                                        <Input 
                                            type="text" 
                                            value={accident?.willLorBeWorked ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Document Dates */}
                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Evrak İstem Tarihi</Label>
                                        <Input type="text" value={formatDate(accident?.documentRequestDate)} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Evrak Alım Tarihi</Label>
                                        <Input type="text" value={formatDate(accident?.documentReceivedDate)} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CollapsibleSection>

                        {/* Insurance Information Section */}
                        <CollapsibleSection
                            title="Sigorta Bilgileri"
                            isOpen={openSections.insurance}
                            onToggle={() => toggleSection('insurance')}
                            icon={<Shield size={20} />}
                        >
                            {/* Client Insurance Information */}
                            <Row>
                                <Col md="12">
                                    <h6 className="mb-3"><i className="fa fa-user me-2"></i>Müvekkil Sigorta Bilgileri</h6>
                                </Col>
                            </Row>
                            
                            {/* Client Traffic Insurance */}
                            <Row>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Müvekkil Trafik Sigorta Sağlayıcısı</Label>
                                        <Input type="text" value={licensePlate?.trafficInsurancePolicy?.trafficInsuranceProvider?.name || 'Belirtilmemiş'} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Müvekkil Trafik Sigorta Numarası</Label>
                                        <Input type="text" value={licensePlate?.trafficInsurancePolicy?.trafficInsuranceNumber || 'Belirtilmemiş'} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Müvekkil Trafik Başlangıç</Label>
                                        <Input type="text" value={formatDate(licensePlate?.trafficInsurancePolicy?.trafficInsuranceStart)} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Müvekkil Trafik Sigorta Vade</Label>
                                        <Input type="text" value={formatDate(licensePlate?.trafficInsurancePolicy?.trafficInsuranceExpiry)} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Client Casco Insurance */}
                            {licensePlate?.cascoInsurancePolicy && (
                                <Row>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Müvekkil Kasko Sağlayıcısı</Label>
                                            <Input type="text" value={licensePlate.cascoInsurancePolicy?.cascoInsuranceProvider?.name || 'Belirtilmemiş'} disabled />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Müvekkil Kasko Numarası</Label>
                                            <Input type="text" value={licensePlate.cascoInsurancePolicy?.cascoInsuranceNumber || 'Belirtilmemiş'} disabled />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Müvekkil Kasko Başlangıç</Label>
                                            <Input type="text" value={formatDate(licensePlate.cascoInsurancePolicy?.cascoInsuranceStart)} disabled />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Müvekkil Kasko Vade</Label>
                                            <Input type="text" value={formatDate(licensePlate.cascoInsurancePolicy?.cascoInsuranceExpiry)} disabled />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Müvekkil Trafik Tahkim Üyesi</Label>
                                        <Input 
                                            type="text" 
                                            value={licensePlate?.trafficInsurancePolicy?.trafficInsuranceProvider?.isArbitrationMember ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                {licensePlate?.cascoInsurancePolicy && (
                                    <Col md="6">
                                        <FormGroup className="mb-3">
                                            <Label>Müvekkil Kasko Tahkim Üyesi</Label>
                                            <Input 
                                                type="text" 
                                                value={licensePlate.cascoInsurancePolicy?.cascoInsuranceProvider?.isArbitrationMember ? 'Evet' : 'Hayır'} 
                                                disabled 
                                            />
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>

                            {/* Counterparty Insurance Information */}
                            <Row>
                                <Col md="12">
                                    <h6 className="mb-3 mt-4"><i className="fa fa-users me-2"></i>Karşı Taraf Sigorta Bilgileri</h6>
                                </Col>
                            </Row>
                            
                            {/* Counterparty Traffic Insurance */}
                            <Row>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Trafik Sigorta Sağlayıcısı</Label>
                                        <Input type="text" value={accident?.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.name || 'Belirtilmemiş'} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Trafik Sigorta Numarası</Label>
                                        <Input type="text" value={accident?.counterpartyTrafficInsurancePolicy?.trafficInsuranceNumber || 'Belirtilmemiş'} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Trafik Başlangıç</Label>
                                        <Input type="text" value={formatDate(accident?.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart)} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Trafik Sigorta Vade</Label>
                                        <Input type="text" value={formatDate(accident?.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry)} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Counterparty Casco Insurance */}
                            {accident?.counterpartyCascoInsurancePolicy && (
                                <Row>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Karşı Taraf Kasko Sağlayıcısı</Label>
                                            <Input type="text" value={accident.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.name || 'Belirtilmemiş'} disabled />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Karşı Taraf Kasko Numarası</Label>
                                            <Input type="text" value={accident.counterpartyCascoInsurancePolicy?.cascoInsuranceNumber || 'Belirtilmemiş'} disabled />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Karşı Taraf Kasko Başlangıç</Label>
                                            <Input type="text" value={formatDate(accident.counterpartyCascoInsurancePolicy?.cascoInsuranceStart)} disabled />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup className="mb-3">
                                            <Label>Karşı Taraf Kasko Vade</Label>
                                            <Input type="text" value={formatDate(accident.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry)} disabled />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Karşı Taraf Trafik Tahkim Üyesi</Label>
                                        <Input 
                                            type="text" 
                                            value={accident?.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.isArbitrationMember ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                {accident?.counterpartyCascoInsurancePolicy && (
                                    <Col md="6">
                                        <FormGroup className="mb-3">
                                            <Label>Karşı Taraf Kasko Tahkim Üyesi</Label>
                                            <Input 
                                                type="text" 
                                                value={accident.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.isArbitrationMember ? 'Evet' : 'Hayır'} 
                                                disabled 
                                            />
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>
                        </CollapsibleSection>

                        {/* File Information Section */}
                        <CollapsibleSection
                            title="Dosya Bilgileri"
                            isOpen={openSections.file}
                            onToggle={() => toggleSection('file')}
                            icon={<FileText size={20} />}
                            badgeText={accidentFile?.fileStatus}
                        >
                            <Row>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Dosya No</Label>
                                        <Input type="text" value={accidentFile?.fileNumber || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Dosya Açılış Tarihi</Label>
                                        <Input type="text" value={formatDate(accidentFile?.fileOpenDate)} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Dosya Durumu</Label>
                                        <Input type="text" value={accidentFile?.fileStatus || ''} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Raporlu</Label>
                                        <Input 
                                            type="text" 
                                            value={accidentFile?.withReport ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Kusurlu</Label>
                                        <Input 
                                            type="text" 
                                            value={accidentFile?.withFault ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Raporsuuz</Label>
                                        <Input 
                                            type="text" 
                                            value={accidentFile?.withoutReport ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Sigorta Başvuru Türü</Label>
                                        <Input type="text" value={accidentFile?.insuranceApplicationType || ''} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label>Sigorta Başvuru Tarihi</Label>
                                        <Input type="text" value={formatDate(accidentFile?.insuranceApplicationDate)} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Files to be Worked */}
                            {accidentFile?.filesToBeWorked && accidentFile.filesToBeWorked.length > 0 && (
                                <Row>
                                    <Col md="12">
                                        <Label>Çalışılacak Dosya Türleri</Label>
                                        {accidentFile.filesToBeWorked.map((file, index) => (
                                            <div key={index} className="mb-2">
                                                <Input 
                                                    type="text" 
                                                    value={`${file.fileType?.[0]?.title || 'Belirtilmemiş'} - ${formatCurrency(file.fileExpense)}`} 
                                                    disabled 
                                                />
                                            </div>
                                        ))}
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Tahkime Hazır</Label>
                                        <Input 
                                            type="text" 
                                            value={accidentFile?.readyForArbitration ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Tahkim Oluşturuldu</Label>
                                        <Input 
                                            type="text" 
                                            value={accidentFile?.arbitrationCreated ? 'Evet' : 'Hayır'} 
                                            disabled 
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label>Son Güncelleme</Label>
                                        <Input type="text" value={formatDate(accidentFile?.lastModified)} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CollapsibleSection>

                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default AccidentFileInformations;
